/**
 * OfflineBanner
 * ---------------------------------------------------------------------
 * A single-line pill that appears when the app loses network connectivity
 * (or when the QA dev panel synthetically flips the offline flag). Sits
 * pinned to the top of the viewport just under the safe-area inset so it
 * never covers the address bar on iOS.
 *
 * Signals it listens for, in priority order:
 *   1. `link:qa:offline` CustomEvent — the QA panel's synthetic override.
 *      This lets the QA account exercise the banner without actually
 *      severing the network (which would break payment flows too).
 *   2. `navigator.onLine === false` — the real thing.
 *   3. Native `offline` / `online` window events — mirror #2 for browsers
 *      that update the flag lazily.
 *
 * The banner is announced via `role="status"` + `aria-live="polite"` so
 * a screen reader picks it up on transition without interrupting current
 * speech (a rider dictating an address shouldn't have that flow blown
 * away just because their phone dropped a cell). Phase B4 layers in a
 * ride-status stepper aria-live channel — this one stays distinct
 * ("network" vs "trip") so they don't stomp each other.
 *
 * Motion: the pill enters/exits with a 220ms slide-from-top when
 * prefers-reduced-motion is off. Under reduced-motion it fades in place
 * with a 60ms opacity transition — verified via CSS-only rules in
 * `src/index.css` so no JS branch is needed.
 */
import React, { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";

const isBrowser = typeof window !== "undefined";

function computeInitialState() {
  if (!isBrowser) return false;
  // navigator.onLine returns TRUE if the browser has ANY route to the
  // network — including LAN-only. It's the standard signal.
  return navigator.onLine === false;
}

export default function OfflineBanner() {
  const [offline, setOffline] = useState(computeInitialState);
  const [qaOverride, setQaOverride] = useState(false);

  useEffect(() => {
    if (!isBrowser) return;
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    const onQa = (e) => setQaOverride(Boolean(e?.detail?.offline));
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("link:qa:offline", onQa);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("link:qa:offline", onQa);
    };
  }, []);

  const visible = offline || qaOverride;
  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 -translate-x-1/2 z-[70] pointer-events-none"
      style={{
        top: "calc(env(safe-area-inset-top, 0px) + 12px)",
        maxWidth: "min(420px, calc(100vw - 24px))",
        width: "100%",
      }}
      data-testid="offline-banner"
    >
      <div className="mx-3 flex items-center gap-2 rounded-full bg-[#0A1128]/95 backdrop-blur-lg px-4 py-2 shadow-[0_10px_28px_rgba(10,17,40,0.28)] border border-white/10 pointer-events-auto link-slide-in-from-top">
        <WifiOff size={13} className="text-[#ed6500] shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-[#F9F7F4] tracking-tight leading-tight">
            You&apos;re offline
          </div>
          <div className="text-[9.5px] text-[#F9F7F4]/60 truncate">
            {qaOverride ? "Synthetic (QA panel) · flip off to restore" : "Some features unavailable · we'll retry automatically"}
          </div>
        </div>
      </div>
    </div>
  );
}
